import styled from '@emotion/styled';
import { Trans } from '@lingui/react/macro';
import { useAtomValue } from 'jotai';
import { xor } from 'lodash';
import { useParams } from 'react-router';

import { trackEvent } from '../../../../app/AppActions';
import { mixpanelEvents } from '../../../../utils/mixpanel';
import { InsFormLabel } from '../../../claims/freeClaims/insuranceExport/styles';
import { toothPartsTogglesAtom } from '../../findingSidebar/atoms';
import type { FindingTypes, FindingsCategory } from '../../types';
import { FINDINGS_CATEGORIES } from '../../types';
import { useGetModelFeatureFlags } from '../../utils';

const StyledList = styled.div`
  .finding-types {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 0.5em;

    .label-field-type {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      cursor: pointer;
      gap: 0.5em;
      width: 16em;
      box-sizing: border-box;
    }

    .label-text-type {
      display: inline-block;
      font-family: 'proxima-nova', sans-serif;
      font-size: 1em;
      font-weight: 500;
      color: white;
    }

    .dialog-checkbox {
      cursor: pointer;
      padding-right: 0.25em;
      box-sizing: border-box;

      &[disabled] {
        cursor: not-allowed;
      }
    }
  }
`;

export const FindingsCheckboxes = ({
  enabledFindingTypes,
  setEnabledFindingTypes,
}: {
  setEnabledFindingTypes: React.Dispatch<React.SetStateAction<FindingTypes>>;
  enabledFindingTypes: FindingTypes;
}) => {
  const {
    isPrlEnabled,
    isRblEnabled,
    isCalculusEnabled,
    isCariesSubclassEnabled,
    isVdaEnabled,
  } = useGetModelFeatureFlags();

  const { id: selectedPatientId } = useParams<{
    id: string;
  }>();

  const toothPartsToggles = useAtomValue(toothPartsTogglesAtom);
  const selectedFindingsCategories: FindingsCategory[] =
    FINDINGS_CATEGORIES.filter(
      (category) =>
        (category === 'restorative' && enabledFindingTypes.caries) ||
        (category === 'incipients' && enabledFindingTypes.incipients) ||
        (category === 'periodontal' &&
          enabledFindingTypes.radiographic_boneloss) ||
        (category === 'endodontic' &&
          enabledFindingTypes.periapical_radiolucency) ||
        (category === 'chart' && toothPartsToggles.tooth_anatomy),
    );
  const handleToggleClick = (category: FindingsCategory, checked: boolean) => {
    const newList = checked
      ? [...selectedFindingsCategories, category] // Add the category if checked
      : selectedFindingsCategories.filter((item) => item !== category); // Remove if unchecked

    const newFindingsTypes: FindingTypes = {
      ...enabledFindingTypes,
      caries: newList.includes('restorative'),
      area_of_concern:
        isCariesSubclassEnabled && newList.includes('restorative'),
      restorative_adjacent_lucency:
        isVdaEnabled && newList.includes('restorative'),
      radiographic_boneloss: isRblEnabled && newList.includes('periodontal'),
      calculus: isCalculusEnabled && newList.includes('periodontal'),
      periapical_radiolucency: isPrlEnabled && newList.includes('endodontic'),
      incipients: newList.includes('incipients'),
    };
    setEnabledFindingTypes(newFindingsTypes);

    // Find which category was toggled
    const toggledCategory = xor(newList, selectedFindingsCategories)[0];
    if (toggledCategory === 'chart') {
      toothPartsToggles.tooth_anatomy = !toothPartsToggles.tooth_anatomy;
    }

    trackEvent(mixpanelEvents.toggleFindings, {
      toggled: {
        category: toggledCategory,
        value: checked,
      },
      previousFindingsTypes: enabledFindingTypes,
      newFindingsTypes: newFindingsTypes,
      patientId: selectedPatientId,
    });
  };
  return (
    <StyledList>
      <div className="finding-types">
        <InsFormLabel className="label-field-type">
          <input
            type="checkbox"
            id="imageFindingsResto"
            data-finding-type="caries"
            className="dialog-checkbox"
            checked={selectedFindingsCategories.includes('restorative')}
            onChange={(e) => handleToggleClick('restorative', e.target.checked)}
          />

          <span className="label-text-type">
            <Trans>Include Carious Lesion AI findings</Trans>
          </span>
        </InsFormLabel>
        <InsFormLabel className="label-field-type">
          <input
            type="checkbox"
            id="imageFindingsIncipient"
            data-finding-type="caries"
            className="dialog-checkbox"
            checked={selectedFindingsCategories.includes('incipients')}
            onChange={(e) => handleToggleClick('incipients', e.target.checked)}
          />

          <span className="label-text-type">
            <Trans>Include Incipient AI findings</Trans>
          </span>
        </InsFormLabel>
        <InsFormLabel className="label-field-type">
          <input
            type="checkbox"
            id="imageFindingsPerio"
            data-finding-type="radiographic_boneloss"
            className="dialog-checkbox"
            checked={selectedFindingsCategories.includes('periodontal')}
            onChange={(e) => handleToggleClick('periodontal', e.target.checked)}
          />

          <span className="label-text-type">
            <Trans>
              Include Radiographic Bone-level & Calculus AI findings
            </Trans>
          </span>
        </InsFormLabel>
        <InsFormLabel className="label-field-type">
          <input
            type="checkbox"
            id="imageFindingsEndo"
            data-finding-type="periapical_radiolucency"
            className="dialog-checkbox"
            checked={selectedFindingsCategories.includes('endodontic')}
            onChange={(e) => handleToggleClick('endodontic', e.target.checked)}
          />

          <span className="label-text-type">
            <Trans>Include Periapical Radiolucency AI findings</Trans>
          </span>
        </InsFormLabel>
        <InsFormLabel className="label-field-type">
          <input
            type="checkbox"
            id="imageFindingsAnatomy"
            data-finding-type="chart"
            className="dialog-checkbox"
            checked={selectedFindingsCategories.includes('chart')}
            onChange={(e) => handleToggleClick('chart', e.target.checked)}
          />

          <span className="label-text-type">
            <Trans>Include Tooth Anatomy</Trans>
          </span>
        </InsFormLabel>
      </div>
    </StyledList>
  );
};
